import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { HBars, PairBars } from "./report/charts";
import { fmtPct, fmtWhen } from "./labels";

const hint = "text-[10px] uppercase tracking-wider text-clay-500 mb-1.5";
const pctS = (v) => (v == null ? "—" : fmtPct(v));

function Progress({ status, onRun }) {
  const job = status?.job;
  const running = job?.status === "running";
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post("/kimkim/patterns/run"); toast.success("Lectura iniciada"); onRun(); }
    catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail) : "No se pudo iniciar"); }
    finally { setBusy(false); }
  };
  if (!running && !status?.pending) return null;
  const pct = running && job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="border border-clay-200 bg-white px-4 py-3 text-xs" data-testid="kk-patterns-progress">
      {running ? (
        <>
          <div className="flex justify-between mb-1.5"><span>Leyendo con IA las conversaciones · {job.done} de {job.total}</span><span className="tabular">{pct} %</span></div>
          <div className="h-1.5 bg-clay-100"><div className="h-full bg-terracotta transition-[width]" style={{ width: `${pct}%` }}/></div>
          <div className="text-clay-500 mt-1.5">Los porcentajes se van completando; vuelve en un rato para el cuadro definitivo.</div>
        </>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span><Sparkles size={12} className="inline mr-1"/> {status.pending} conversaciones cerradas sin leer · coste estimado ≈ {String(status.estimated_cost_eur).replace(".", ",")} €</span>
          <button type="button" onClick={run} disabled={busy} className="border border-clay-900 bg-clay-900 text-clay-50 px-3 py-1 hover:bg-terracotta disabled:opacity-50" data-testid="kk-patterns-run">Leer con IA</button>
        </div>
      )}
    </div>
  );
}

function useLineAi(line, queryParams, enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try { const { data: d } = await api.get("/kimkim/patterns/ai", { params: { ...queryParams, line, force: force ? 1 : 0 } }); setData(d); }
    catch (e) { const d = e?.response?.data?.detail; setError(typeof d === "string" ? d : "No se pudo redactar"); }
    finally { setLoading(false); }
  }, [line, queryParams]);
  useEffect(() => { setData(null); if (enabled) load(); }, [load, enabled]);
  return { data, loading, error, regenerate: () => load(true) };
}

function Synthesis({ line, queryParams, enabled, waiting }) {
  const { data, loading, error, regenerate } = useLineAi(line, queryParams, enabled);
  return (
    <div className="space-y-3" data-testid={`kk-pat-l${line}-ai`}>
      <div className="flex items-center justify-between gap-2">
        <div className="smallcaps flex items-center gap-1.5"><Sparkles size={11}/> Patrones según la IA</div>
        {data && <button type="button" onClick={regenerate} disabled={loading} className="text-[11px] text-clay-500 hover:text-clay-900 inline-flex items-center gap-1" data-testid={`kk-pat-l${line}-regen`}><RefreshCw size={10} className={loading ? "animate-spin" : ""}/> Regenerar</button>}
      </div>
      {!enabled && <div className="text-sm text-clay-500">{waiting ? "Se redactará cuando termine la lectura de las conversaciones." : "Hacen falta al menos 10 lecturas en esta línea."}</div>}
      {enabled && !data && !error && <div className="text-sm text-clay-500">Redactando…</div>}
      {error && <div className="text-sm text-terracotta">{error} <button onClick={() => regenerate()} className="underline ml-1">Reintentar</button></div>}
      {data && (
        <div className={loading ? "opacity-60" : ""}>
          <ol className="space-y-1.5 text-sm leading-relaxed list-decimal pl-5">{data.patrones.map((s, i) => <li key={i}>{s}</li>)}</ol>
          <div className="border-l-4 border-terracotta pl-4 py-1 mt-4">
            <div className="smallcaps mb-1">Qué hacer</div>
            <ul className="space-y-1 text-sm">{data.acciones.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <div className="text-[11px] text-clay-400 mt-2">Generado {fmtWhen(data.generated_at)} · {data.model}</div>
        </div>
      )}
    </div>
  );
}

function Examples({ items, line }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className="mt-5">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs text-clay-600 hover:text-clay-900" data-testid={`kk-pat-l${line}-examples-toggle`}>
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>} {open ? "Ocultar ejemplos" : `Ver ${items.length} ejemplos`}
      </button>
      {open && (
        <ul className="mt-3 space-y-2" data-testid={`kk-pat-l${line}-examples`}>
          {items.map((e) => (
            <li key={e.lead_id} className="text-[12px] border-l-2 border-clay-300 pl-3">
              <Link to={`/admin/kimkim-leads/${e.lead_id}`} className="font-medium hover:text-terracotta">{e.traveler_name || e.lead_id}</Link>
              <span className="text-clay-700"> — {e.summary}</span>
              {e.key_factor && <div className="text-clay-500">Clave: {e.key_factor}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SOLD_LOST = [{ key: "sold_pct", label: "Vendidos", tone: "bg-terracotta", format: pctS }, { key: "lost_pct", label: "No vendidos", tone: "bg-clay-400", format: pctS }];

function FitFields({ fields, contrast, line }) {
  return (
    <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
      {fields.map((f) => (
        <div key={f.key} data-testid={`kk-pat-l${line}-${f.key}`}>
          <div className={hint}>{f.label}</div>
          {contrast
            ? <PairBars testid={`kk-pat-l${line}-${f.key}-bars`} labelW="w-36" rows={f.values.map((v) => ({ ...v, key: v.key, label: v.label }))} series={SOLD_LOST}/>
            : <HBars compact testid={`kk-pat-l${line}-${f.key}-bars`} labelW="w-36 md:w-44" rows={f.values.map((v) => ({ key: v.key, label: v.label, value: v.pct || 0, text: pctS(v.pct), muted: !v.count }))}/>}
        </div>
      ))}
    </div>
  );
}

function PriceChange({ pc }) {
  if (!pc?.n) return null;
  return (
    <p className="text-[12px] text-clay-700 mt-4" data-testid="kk-pat-price-change">
      En los {pc.n} casos con dos o más propuestas, el precio final <strong>bajó</strong> en el {pctS(pc.down_pct)}, <strong>subió</strong> en el {pctS(pc.up_pct)} y se mantuvo en el {pctS(pc.same_pct)} (variación mediana {String(pc.median_pct).replace(".", ",")} %).
    </p>
  );
}

function TopChanges({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className={hint}>Cambios más repetidos entre lo pedido y lo propuesto</div>
      <ul className="text-[12px] space-y-0.5">{items.slice(0, 8).map((c) => <li key={c.text}><span className="tabular text-clay-500 inline-block w-7">{c.count}×</span>{c.text}</li>)}</ul>
    </div>
  );
}

function ConvCharts({ block, contrast, line }) {
  const top = block.patterns.filter((p) => (contrast ? (p.sold_n || p.lost_n) : p.count)).slice(0, 14);
  return (
    <div className="space-y-5">
      <div>
        <div className={hint}>{contrast ? "Qué pasa en la conversación · vendidos frente a no vendidos" : "Qué pasa en la conversación · % de los vendidos"}</div>
        {contrast
          ? <PairBars testid={`kk-pat-l${line}-patterns`} labelW="w-44 md:w-56" rows={top.map((p) => ({ ...p, key: p.key, label: p.label }))} series={SOLD_LOST}/>
          : <HBars testid={`kk-pat-l${line}-patterns`} labelW="w-44 md:w-56" rows={top.map((p) => ({ key: p.key, label: p.label, value: p.pct || 0, text: pctS(p.pct), sub: `· ${p.count}` }))}/>}
      </div>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
        <div>
          <div className={hint}>Tono de la agente</div>
          {contrast
            ? <PairBars testid={`kk-pat-l${line}-tone`} labelW="w-24" rows={block.tone.map((v) => ({ ...v, key: v.key, label: v.label }))} series={SOLD_LOST}/>
            : <HBars compact testid={`kk-pat-l${line}-tone`} labelW="w-24" rows={block.tone.map((v) => ({ key: v.key, label: v.label, value: v.pct || 0, text: pctS(v.pct) }))}/>}
        </div>
        {contrast && (
          <div>
            <div className={hint}>Cómo acabó</div>
            <HBars compact testid={`kk-pat-l${line}-close`} labelW="w-44" rows={block.close.filter((v) => v.count).map((v) => ({ key: v.key, label: v.label, value: v.pct || 0, text: pctS(v.pct) }))}/>
          </div>
        )}
      </div>
    </div>
  );
}

function LineBlock({ idx, line, title, n, children, queryParams, examples, waiting }) {
  return (
    <section className="bg-white border border-clay-200 px-6 py-6 md:px-8 md:py-7" data-testid={`kk-pat-l${line}`}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="font-serif text-3xl text-terracotta leading-none tabular">{idx}</span>
        <h2 className="font-serif text-xl md:text-2xl leading-tight">{title}</h2>
        <span className="text-[11px] text-clay-500 ml-auto whitespace-nowrap">{n} conversaciones leídas</span>
      </div>
      <div className="grid lg:grid-cols-[3fr_2fr] gap-8">
        <div>{children}<Examples items={examples} line={line}/></div>
        <Synthesis line={line} queryParams={queryParams} enabled={n >= 10 && !waiting} waiting={waiting}/>
      </div>
    </section>
  );
}

export function Patterns({ queryParams }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { const { data: d } = await api.get("/kimkim/patterns", { params: queryParams }); setData(d); }
    catch (e) { const d = e?.response?.data?.detail; setError(typeof d === "string" ? d : "No se pudieron calcular los patrones"); }
  }, [queryParams]);
  useEffect(() => { load(); }, [load]);
  const running = data?.status?.job?.status === "running";
  useEffect(() => { if (!running) return; const t = setInterval(load, 30000); return () => clearInterval(t); }, [running, load]);
  if (error && !data) return <div className="text-sm text-terracotta py-8" data-testid="kk-patterns-error">{error} <button onClick={load} className="underline ml-2">Reintentar</button></div>;
  if (!data) return <div className="text-sm text-clay-600 py-8" data-testid="kk-patterns-loading">Calculando los patrones…</div>;
  const { l1, l2, l3, l4, counts } = data;
  const aiParams = { date_from: queryParams.date_from, date_to: queryParams.date_to, agent: queryParams.agent };
  return (
    <div className="space-y-6" data-testid="kk-patterns">
      <Progress status={data.status} onRun={load}/>
      <div className="text-[11px] text-clay-500" data-testid="kk-patterns-scope">
        Lecturas con IA de cada conversación cerrada: <strong className="tabular">{counts.sold}</strong> vendidos y <strong className="tabular">{counts.lost}</strong> no vendidos que siguieron hablando tras la primera propuesta.
        «Último contacto» = la última propuesta enviada y los mensajes posteriores.
      </div>
      <LineBlock idx={1} line="1" title={l1.title} n={l1.n} queryParams={aiParams} examples={l1.examples} waiting={running}>
        <FitFields fields={l1.fields} line="1"/><PriceChange pc={l1.price_change}/><TopChanges items={l1.top_changes}/>
      </LineBlock>
      <LineBlock idx={2} line="2" title={l2.title} n={l2.n} queryParams={aiParams} examples={l2.examples} waiting={running}>
        <FitFields fields={l2.fields} contrast line="2"/><PriceChange pc={l2.price_change}/><TopChanges items={l2.top_changes}/>
      </LineBlock>
      <LineBlock idx={3} line="3" title={l3.title} n={l3.n} queryParams={aiParams} examples={l3.examples} waiting={running}>
        <ConvCharts block={l3} line="3"/>
      </LineBlock>
      <LineBlock idx={4} line="4" title={l4.title} n={l4.n} queryParams={aiParams} examples={l4.examples} waiting={running}>
        <ConvCharts block={l4} contrast line="4"/>
      </LineBlock>
    </div>
  );
}
